import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';
import {
  Button,
  CheckboxField,
  LinkButton,
  SavingIndicator,
  showMessageBox,
  Text,
  TextField,
} from '../../../components';
import { useAccount, useIsAdmin, useStore } from '../../../store';
import Squarespace from '../store/services/webhooks/Squarespace';
import Webhooks from '../store/services/webhooks/Webhooks';

export default observer(function SquarespaceForm({
  webhooks,
}: {
  webhooks: Webhooks;
}) {
  const { appConfig } = useStore();
  const { id: userId } = useAccount();
  const isAdmin = useIsAdmin({ mode: 'main' });

  const webhook = webhooks.selectedServices[0] as Squarespace;
  const isIntegrated = Boolean(webhook.accessToken);

  const url = new URL(
    `api/v1/webhooks/squarespace/oauth/step1?providerId=${userId}`,
    appConfig.apiEndpointUrl,
  ).href;

  async function disableIntegration() {
    const buttonId = await showMessageBox({
      buttons: [
        { id: 'ok', title: 'Disable integration' },
        { id: 'cancel', title: 'Cancel' },
      ],
      content: (
        <>
          After disabling integration orders form Squarespace can&apos;t be
          processed by Pulse.
        </>
      ),
      title: 'Are you sure?',
    });

    if (buttonId === 'ok') {
      await webhook.delete();
    }
  }

  return (
    <div>
      {isIntegrated && (
        <Box sx={{ mt: 2 }}>
          <TextField disabled label="Token" value={webhook.accessToken} />
          <TextField disabled label="Website ID" value={webhook.websiteId} />

          <Button
            color="secondary"
            icon="squarespace"
            onClick={disableIntegration}
            sx={{ mt: 2 }}
            type="primary"
          >
            Disable integration
          </Button>

          {isAdmin && (
            <div>
              <CheckboxField
                helperText={
                  <>
                    Helpful when old orders are stuck after re-integrating.
                    &nbsp;
                    <Text inline variant="error">
                      Should be disabled after orders are processed.
                    </Text>
                  </>
                }
                label="Allow processing unauthorized webhook calls"
                name="allowUnauthorized"
                onChange={webhook.handleChange}
                value={webhook.allowUnauthorized}
              />
            </div>
          )}
        </Box>
      )}
      {!isIntegrated && (
        <LinkButton
          external={false}
          href={url}
          icon="squarespace"
          sx={{ mt: 2 }}
          type="primary"
        >
          Integrate with Squarespace
        </LinkButton>
      )}

      <SavingIndicator indicator={webhook.indicator} sx={{ mt: 2 }} />
    </div>
  );
});
